<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardMedias from "@/components/shared/CardMedias.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TableImage from "@/components/shared/TableImage.vue"
import router from "@/plugins/router"
import qualityService from "@/services/quality"
import type { Product } from "@/types/product.type"
import type { QualityDetail } from "@/types/quality.type"
import { formattedMoney } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import { unitedNumber } from "@/utils/string"

const route = useRoute()
const { t } = useI18n()

const item = ref<QualityDetail | null>(null)
const id = computed(() => route.params.id)
const pageTitle = computed(() => {
  return `${t("item.title")} #${item.value?.name || ""}`
})
const internalMedias = computed(() => {
  const medias = []
  if (item.value && item.value.storeSwatchImage) medias.push(item.value.storeSwatchImage)
  return medias
})

const colorProducts = ref<Product[]>([])

async function loadItem(itemId: number) {
  item.value = await qualityService.get(itemId)
}

async function loadProducts(itemId: number) {
  const data = await qualityService.products(itemId)
  colorProducts.value = data.products
}

onMounted(async () => {
  // id.value is bound to the live route, not a snapshot — if the user
  // navigates away while this is still running, route.params.id changes
  // out from under the second await, sending Number(undefined) = NaN
  // (Sentry: qualities/NaN/products, partner-frontend issue 7647807510)
  const itemId = Number(id.value)
  try {
    await loadItem(itemId)
    await loadProducts(itemId)
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/items")
  }
})
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <Card v-if="item">
      <CardTitle>
        <template #default>
          {{ t("item.medias") }}
        </template>
        <!-- <template #actions>
          <Button type="link" :path="`/items/${item.id}/edit_medias`" :text="$t('shared.edit')" color="warning"
            size="sm" />
        </template> -->
      </CardTitle>
      <CardSubtitle v-if="internalMedias.length > 0">{{ t("item.mediaTypes.internal") }}</CardSubtitle>
      <CardMedias v-if="internalMedias.length > 0" :medias="internalMedias" />
      <CardSubtitle v-if="item.medias.length > 0">{{ t("item.mediaTypes.advertisement") }}</CardSubtitle>
      <CardMedias v-if="item.medias.length > 0" :medias="item.medias" />
    </Card>
    <Card v-if="item">
      <CardTitle>
        <template #default>
          {{ t("item.specifications") }}
        </template>
        <template #actions>
          <Button
            type="link"
            :path="`/items/${item.id}/edit_specs`"
            :text="$t('shared.edit')"
            color="warning"
            size="sm"
          />
        </template>
      </CardTitle>
      <div class="row">
        <div class="col-12 col-lg-6 col-xxl-4 mb-4">
          <CardSubtitle>{{ t("item.basic") }}</CardSubtitle>
          <CardContent>
            <InfoTable>
              <InfoRow :name="$t('item.name')">{{ item.name }}</InfoRow>
              <InfoRow :name="$t('item.code')">{{ item.code }}</InfoRow>
              <InfoRow :name="$t('item.hsCode')">{{ item.hsCode }}</InfoRow>
              <InfoRow :name="$t('item.countryOfOrigin')">{{ item.countryOfOrigin?.name }}</InfoRow>
              <InfoRow :name="$t('item.isPrintOrderable')">{{ t(`shared.boolean.${item.isPrintOrderable}`) }}</InfoRow>
              <InfoRow :name="$t('item.status')">
                <StatusBadge model="item" field="status" :value="item.status" />
              </InfoRow>
            </InfoTable>
          </CardContent>
        </div>
        <div class="col-12 col-lg-6 col-xxl-4 mb-4">
          <CardSubtitle>{{ t("item.dimensions") }}</CardSubtitle>
          <CardContent>
            <InfoTable>
              <InfoRow :name="$t('item.minimumWidth')"
                >{{ unitedNumber(item.minimumWidth, t("item.units.minimumWidth")) }}
              </InfoRow>
              <InfoRow :name="$t('item.fullWidth')"
                >{{ unitedNumber(item.fullWidth, t("item.units.fullWidth")) }}
              </InfoRow>
              <InfoRow :name="$t('item.weight')">{{ unitedNumber(item.weight, t("item.units.weight")) }}</InfoRow>
              <InfoRow :name="$t('item.yardWeight')"
                >{{ unitedNumber(item.yardWeight, t("item.units.yardWeight")) }}
              </InfoRow>
              <InfoRow :name="$t('item.thickness')"
                >{{ unitedNumber(item.thickness, t("item.units.thickness")) }}
              </InfoRow>
            </InfoTable>
          </CardContent>
        </div>
        <div class="col-12 col-lg-6 col-xxl-4 mb-4">
          <CardSubtitle>{{ t("item.tags") }}</CardSubtitle>
          <CardContent>
            <InfoTable>
              <InfoRow :name="t('item.category')">{{ item.category?.name }}</InfoRow>
              <InfoRow :name="t('item.pattern')">{{ item.patterns.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.dyeMethod')">{{ item.dyeMethod?.name }}</InfoRow>
              <InfoRow :name="t('item.contents')">
                {{ item.contents.map((e) => `${e.name} ${e.ratio}%`).join(" ") }}
              </InfoRow>
              <InfoRow :name="t('item.yarnEffects')">{{ item.yarnEffects.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.brandYarns')">{{ item.brandYarns.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.finishes')">{{ item.finishes.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.appearances')">{{ item.appearances.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.performances')">{{ item.performances.map((e) => e.name).join(", ") }}</InfoRow>
              <InfoRow :name="t('item.sustainabilityFactors')">
                {{ item.sustainabilityFactors.map((e) => e.name).join(",") }}
              </InfoRow>
            </InfoTable>
          </CardContent>
        </div>
        <div class="col-12 col-lg-6 col-xxl-12">
          <CardSubtitle>{{ t("item.sellerMemo") }}</CardSubtitle>
          <CardContent>
            {{ item.sellerMemo || "-" }}
          </CardContent>
        </div>
      </div>
    </Card>
    <Card v-if="item">
      <CardTitle>
        <template #default>
          {{ t("item.pricing") }}
        </template>
        <template #actions>
          <Button
            type="link"
            :path="`/items/${item.id}/edit_prices`"
            :text="$t('shared.edit')"
            color="warning"
            size="sm"
          />
        </template>
      </CardTitle>
      <div class="row">
        <div class="col mb-4">
          <CardSubtitle v-if="item.isPrintOrderable">{{ t("item.priceSalesType.standardOrder") }}</CardSubtitle>
          <CardContent v-if="item.standardOrderPriceTagGroup">
            <Table>
              <template #header>
                <tr>
                  <th>{{ t("item.priceTag.range") }}</th>
                  <th>{{ t("item.priceTag.quantity") }}</th>
                  <th>{{ t("item.priceTag.price") }}</th>
                </tr>
              </template>
              <template #default>
                <tr v-for="(priceTag, index) in item.standardOrderPriceTagGroup.qualityPriceTags" :key="index">
                  <td>{{ t(`item.priceTag.rangeType.${priceTag.range}`) }}</td>
                  <td>
                    {{ unitedNumber(priceTag.quantityGteq, $t("item.units.yards_short")) }}
                    <span v-if="priceTag.quantityLteq">
                      - {{ unitedNumber(priceTag.quantityLteq, $t("item.units.yards_short")) }}
                    </span>
                    <span v-else>
                      {{ $t("item.priceTag.quantityAndUp") }}
                    </span>
                  </td>
                  <td>{{ formattedMoney(priceTag.price, item.standardOrderPriceTagGroup.currency) }}</td>
                </tr>
              </template>
            </Table>
          </CardContent>
        </div>
        <div class="col" v-if="item.isPrintOrderable">
          <CardSubtitle>{{ t("item.priceSalesType.print") }}</CardSubtitle>
          <CardContent v-if="item.printPriceTagGroup">
            <Table>
              <template #header>
                <tr>
                  <th>{{ t("item.priceTag.range") }}</th>
                  <th>{{ t("item.priceTag.quantity") }}</th>
                  <th>{{ t("item.priceTag.price") }}</th>
                </tr>
              </template>
              <template #default>
                <tr v-for="(priceTag, index) in item.printPriceTagGroup.qualityPriceTags" :key="index">
                  <td>{{ t(`item.priceTag.rangeType.${priceTag.range}`) }}</td>
                  <td>
                    {{ unitedNumber(priceTag.quantityGteq, $t("item.units.yards_short")) }}
                    <span v-if="priceTag.quantityLteq">
                      - {{ unitedNumber(priceTag.quantityLteq, $t("item.units.yards_short")) }}
                    </span>
                    <span v-else>
                      {{ $t("item.priceTag.quantityAndUp") }}
                    </span>
                  </td>
                  <td>{{ formattedMoney(priceTag.price, item.printPriceTagGroup.currency) }}</td>
                </tr>
              </template>
            </Table>
          </CardContent>
        </div>
      </div>
    </Card>
    <Card v-if="item && colorProducts.length > 0">
      <CardTitle>
        <template #default> {{ t("colorProducts") }} ({{ colorProducts.length }}) </template>
        <template #actions>
          <Button
            type="link"
            :path="`/items/${item.id}/edit_colors`"
            :text="$t('shared.edit')"
            color="warning"
            size="sm"
          />
        </template>
      </CardTitle>
      <Table>
        <template #header>
          <tr>
            <th>{{ t("colorProduct.image") }}</th>
            <th>{{ t("colorProduct.colorCode") }}</th>
            <th>{{ t("colorProduct.colors") }}</th>
            <th>{{ t("colorProduct.priceSurplus") }}</th>
            <th>{{ t("colorProduct.status") }}</th>
          </tr>
        </template>
        <template #default>
          <tr v-for="(product, idx) in colorProducts" :key="idx">
            <td>
              <TableImage v-if="product.image" :image="product.image" />
              <span v-else>-</span>
            </td>
            <td>{{ product.colorCode }}</td>
            <td>{{ product.colors.map((e) => e.name).join(", ") }}</td>
            <td>{{ formattedMoney(product.priceSurplus, item.standardOrderPriceTagGroup?.currency || "krw") }}</td>
            <td>
              <StatusBadge model="colorProduct" field="status" :value="product.status" />
            </td>
          </tr>
        </template>
      </Table>
    </Card>
  </LayoutAuthed>
</template>

<style scoped></style>
